import { useEffect, useRef, useState } from 'react';
import { demoUpload, getGlobalStats } from '../../services/api.js';
import { listGuestSessions, removeGuestSession } from '../../services/guestSessions.js';

const ACCEPT = '.csv,.vbo,.xrk,.ld,.mov,.mp4';

const wrap = {
  minHeight: '100vh', background: '#0f1117', color: '#e5e7eb',
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  padding: '3rem 1.5rem', gap: '1.5rem',
};

/**
 * No-login landing inside the guest sandbox: a drop zone for telemetry files
 * plus the browser-local list of this visitor's previous demo uploads.
 *
 * onOpenSession(id, name, date) is provided by App — it sets the read token,
 * records the session in localStorage, and loads it into the analysis view.
 */
export default function GuestHome({ onOpenSession }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [error, setError]         = useState('');
  const [sessions, setSessions]   = useState(() => listGuestSessions());
  const [dragOver, setDragOver]   = useState(false);
  const [stats, setStats]         = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { getGlobalStats().then(setStats); }, []);

  async function handleFile(file) {
    if (!file || uploading) return;
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const payload = await demoUpload(file, setProgress);
      onOpenSession(payload.session_id, payload?.meta?.session_name, payload?.meta?.date);
    } catch (err) {
      let msg = err?.message || 'Upload failed';
      try { msg = JSON.parse(msg).detail || msg; } catch {}
      setError(msg);
      setUploading(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }

  function handleRemove(id) {
    setSessions(removeGuestSession(id));
  }

  return (
    <div style={wrap}>
      <img src="/TDA_Logo.jpg" alt="TrackDay Analysis" style={{ height: 56, borderRadius: 8 }} />
      <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Try it — no signup</h1>
      <p style={{ margin: 0, color: '#9ca3af', textAlign: 'center', maxWidth: 520 }}>
        Drop a telemetry file to see the full analysis — track map, speed &amp; G traces,
        and per-corner metrics. CSV, VBO, AiM XRK, MoTeC, or an AiM SmartyCam / SmartyCam3
        video (we read the telemetry embedded in it).
      </p>

      {stats && stats.total_laps > 0 && (
        <div style={{ color: '#9ca3af', fontSize: '0.9rem', textAlign: 'center' }}>
          <strong style={{ color: '#60a5fa' }}>{stats.total_laps.toLocaleString()}</strong> laps analyzed
          {' · '}
          <strong style={{ color: '#60a5fa' }}>{stats.total_sessions.toLocaleString()}</strong> sessions
          {' · '}
          <strong style={{ color: '#60a5fa' }}>{stats.total_tracks.toLocaleString()}</strong> tracks
        </div>
      )}

      <div
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          width: 'min(560px, 92vw)', padding: '2.5rem 1.5rem', borderRadius: 12,
          border: `2px dashed ${dragOver ? '#2563eb' : '#374151'}`,
          background: dragOver ? '#0f1b33' : '#141821',
          textAlign: 'center', cursor: uploading ? 'default' : 'pointer',
          transition: 'border-color .15s, background .15s',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          style={{ display: 'none' }}
          onChange={e => handleFile(e.target.files?.[0])}
        />
        {uploading ? (
          <div>
            <div style={{ marginBottom: 10 }}>Analyzing… {progress}%</div>
            <div style={{ height: 6, background: '#1f2937', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ width: `${progress}%`, height: '100%', background: '#2563eb' }} />
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>Drop a file here</div>
            <div style={{ color: '#9ca3af', marginTop: 4 }}>or click to browse</div>
          </>
        )}
      </div>

      {error && <p style={{ color: '#f87171', margin: 0 }}>{error}</p>}

      {sessions.length > 0 && (
        <div style={{ width: 'min(560px, 92vw)' }}>
          <div style={{ color: '#9ca3af', fontSize: '0.85rem', margin: '0 0 8px' }}>
            Your uploads (this browser)
          </div>
          {sessions.map(s => (
            <div
              key={s.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                background: '#141821', border: '1px solid #1f2937', borderRadius: 8, marginBottom: 6,
              }}
            >
              <button
                onClick={() => onOpenSession(s.id, s.name, s.date)}
                style={{
                  flex: 1, textAlign: 'left', background: 'none', border: 'none',
                  color: '#e5e7eb', cursor: 'pointer', minWidth: 0,
                }}
              >
                <span style={{ fontWeight: 600 }}>{s.name}</span>
                {s.date && <span style={{ color: '#6b7280', marginLeft: 8 }}>{s.date}</span>}
              </button>
              <button
                onClick={() => handleRemove(s.id)}
                title="Remove from this list"
                style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '1.1rem' }}
              >×</button>
            </div>
          ))}
        </div>
      )}

      <p style={{ margin: '0.5rem 0 0', color: '#9ca3af', fontSize: '0.9rem' }}>
        <a href="/app" onClick={(e) => { e.preventDefault(); window.location.replace('/app'); }}
           style={{ color: '#60a5fa', fontWeight: 600, textDecoration: 'none' }}>
          Sign up to save your data on any device →
        </a>
      </p>
    </div>
  );
}
